import React from 'react';
import PendingPurchaseModalWrapper from './PendingPurchaseModalWrapper';
import PurchaseHeaderView from '../../purchases/viewTables/PurchaseHeaderView';
import PaymentConditionViewSection from '../../purchases/viewTables/PaymentConditionViewSection';
import PurchaseProductsViewTable from '../../purchases/viewTables/standard/PurchaseProductsViewTable';
import ImportProductsViewTable from '../../purchases/viewTables/import/ImportProductsViewTable';
import PurchaseExtraInfoView from '../../purchases/viewTables/PurchaseExtraInfoView';
import TextAreaField from '../../../components/Forms/Input/TextAreaField';
import { useBasePurchase } from '../../../hooks/purchases/useBasePurchase';
import { DOCUMENT_TYPES } from '../../../constants/purchase';
import { purchaseColumnsMap } from '../../../features/document/columns/purchases';

const PurchaseViewModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  showCancellationControls?: boolean;
}> = ({ isOpen, onClose, showCancellationControls = false }) => {
  const { form, updateField, handlerCancelPurchase } = useBasePurchase();
  const documentTypeId = form.documentType?.id;

  // 🔥 Aquí seguimos usando la lógica normal, pero con un mapa
  const getColumnsByDocumentType = () => {
    switch (documentTypeId) {
      case DOCUMENT_TYPES.TAX_CREDIT:
        return purchaseColumnsMap.TAX_CREDIT;
      case DOCUMENT_TYPES.DEBIT_NOTE:
        return purchaseColumnsMap.DEBIT_NOTE;
      case DOCUMENT_TYPES.CREDIT_NOTE:
        return purchaseColumnsMap.CREDIT_NOTE;
      case DOCUMENT_TYPES.EXCLUDED_SUBJECT:
        return purchaseColumnsMap.EXCLUDED_SUBJECT;
      case DOCUMENT_TYPES.IMPORT:
        return purchaseColumnsMap.IMPORT;
      default:
        return purchaseColumnsMap.STANDARD;
    }
  };

  const columns = getColumnsByDocumentType();

  const handleCancel = async () => {
    await handlerCancelPurchase(form.id!);
    onClose();
  };

  return (
    <PendingPurchaseModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Detalle de la Compra"
      maxWidth="max-w-6xl"
    >
      <div className="text-gray-800 dark:text-gray-100 text-sm">
        {/* 🧾 Encabezado */}
        <PurchaseHeaderView purchase={form} />

        {/* 📦 Tabla de productos */}
        {documentTypeId === DOCUMENT_TYPES.IMPORT ? (
          <ImportProductsViewTable
            columns={columns}
            data={form.products}
            noDataMessage="No hay productos de importación."
          />
        ) : (
          <PurchaseProductsViewTable
            columns={columns}
            data={form.products}
            noDataMessage="No hay productos agregados."
          />
        )}

        {/* 💳 Condiciones de pago */}
        {documentTypeId !== DOCUMENT_TYPES.IMPORT &&
          documentTypeId !== DOCUMENT_TYPES.DEBIT_NOTE &&
          documentTypeId !== DOCUMENT_TYPES.CREDIT_NOTE && (
            <PaymentConditionViewSection />
          )}

        {/* Datos adicionales de importación */}
        {documentTypeId === DOCUMENT_TYPES.IMPORT && <PurchaseExtraInfoView />}

        {/* 🛑 Razón de anulación (visualización) */}
        {form.cancellationReason && !showCancellationControls && (
          <div className="mt-6 space-y-6 rounded border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
            <TextAreaField
              name="cancellationReason"
              label="Razón de Anulación"
              placeholder="Motivo por el cual se anuló la compra"
              value={form.cancellationReason}
              onChange={() => {}}
              iconKey='edit'
              disabled
            />
          </div>
        )}

        {/* ✍ Campo editable si se está anulando */}
        {showCancellationControls && (
          <div className="mt-6 space-y-6 rounded border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
            <TextAreaField
              name="cancellationReason"
              label="Razón de Anulación"
              placeholder="Ingrese el motivo de anulación de la compra"
              value={form.cancellationReason || ''}
              iconKey='edit'
              onChange={(e) =>
                updateField('cancellationReason', e.target.value)
              }
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded bg-meta-1 px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
              >
                Anular Compra
              </button>
            </div>
          </div>
        )}
      </div>
    </PendingPurchaseModalWrapper>
  );
};

export default PurchaseViewModal;
